import { useEffect, useRef, useState } from "react";

/**
 * Animate an integer counter from 0 -> `end` once the element enters the viewport.
 * Supports decimal targets (e.g., 19.0) and custom suffix/prefix formatting via `format`.
 */
export default function useCountUp({ end, duration = 1800, decimals = 0 } = {}) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || started) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setStarted(true);
            const start = performance.now();
            const step = (now) => {
              const t = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              const next = eased * end;
              setValue(Number(next.toFixed(decimals)));
              if (t < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration, decimals, started]);

  return { ref, value };
}
